import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Zap, Phone, MessageSquare, Mic, BarChart3, DollarSign, TrendingUp } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

function PayPalPay({ type, item_id, onCaptured }) {
  return (
    <PayPalButtons
      style={{ layout: "horizontal", color: "blue", shape: "rect", label: "pay", height: 38, tagline: false }}
      forceReRender={[item_id]}
      createOrder={async () => {
        const { data } = await api.post("/paypal/create-order", { type, item_id });
        return data.order_id;
      }}
      onApprove={async (data) => {
        try {
          const { data: cap } = await api.post(`/paypal/capture-order/${data.orderID}`);
          if (cap.payment_status === "paid") {
            toast.success("Payment captured via PayPal");
            onCaptured && onCaptured();
          } else {
            toast.error("Payment not completed");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Capture failed");
        }
      }}
      onError={() => toast.error("PayPal error")}
    />
  );
}

export default function Billing() {
  const [data, setData] = useState(null);
  const [packages, setPackages] = useState(null);
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cost, setCost] = useState(null);

  const load = async () => {
    const [me, pk, ub, ca] = await Promise.all([
      api.get("/billing/me"),
      api.get("/billing/packages"),
      api.get("/billing/usage-breakdown"),
      api.get("/billing/cost-analysis"),
    ]);
    setData(me.data); setPackages(pk.data); setUsage(ub.data); setCost(ca.data);
  };

  useEffect(() => {
    load();
    api.get("/paypal/config").then((r) => setPaypalCfg(r.data));
  }, []);

  // Handle Stripe success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) {
      api.get(`/billing/session/${sid}`).then(({ data }) => {
        if (data.payment_status === "paid") {
          toast.success("Payment confirmed");
          window.history.replaceState({}, "", "/dashboard/billing");
          load();
        } else {
          let attempts = 0;
          const poll = () => {
            attempts++;
            api.get(`/billing/session/${sid}`).then(({ data: d2 }) => {
              if (d2.payment_status === "paid") { toast.success("Payment confirmed"); load(); }
              else if (attempts < 5) setTimeout(poll, 2000);
            });
          };
          setTimeout(poll, 2000);
        }
      });
    }
  }, []);

  const stripeCheckout = async (type, item_id) => {
    const t = toast.loading("Creating checkout…");
    try {
      const { data } = await api.post("/billing/checkout", {
        type, item_id, origin_url: window.location.origin,
      });
      toast.dismiss(t);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed", { id: t });
    }
  };

  if (!data || !packages || !paypalCfg) return <div className="text-sm text-[#4B5563]">Loading…</div>;

  return (
    <PayPalScriptProvider
      options={{
        clientId: paypalCfg.client_id,
        currency: "USD",
        intent: "capture",
      }}
    >
      <div data-testid="billing-page" className="space-y-6">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Billing & Credits</h1>
          <p className="text-sm text-[#4B5563]">Manage your plan, top up credits and review invoices.</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-4">
          <div className="hs-card p-6">
            <div className="text-xs font-mono uppercase text-[#4B5563]">current plan</div>
            <div className="font-display font-extrabold text-2xl mt-2">{data.package.name}</div>
            <div className="text-xs text-[#4B5563] mt-1">${data.package.price_usd}/mo · {data.subscription_status}</div>
            <div className="text-[10px] font-mono text-[#4B5563] mt-2">agents: {data.agent_limit || 1} max</div>
          </div>
          <div className="hs-card p-6">
            <div className="text-xs font-mono uppercase text-[#4B5563] flex items-center gap-1"><Zap className="w-3 h-3" /> chats</div>
            <div className="font-display font-extrabold text-3xl mt-2">{data.ai_credits}</div>
            <div className="text-xs text-[#4B5563] mt-1">{data.ai_credits_used || 0} replies sent</div>
          </div>
          <div className="hs-card p-6">
            <div className="text-xs font-mono uppercase text-[#4B5563] flex items-center gap-1">🎙 web voice</div>
            <div className="font-display font-extrabold text-3xl mt-2">{Math.round((data.voice_credits || 0) / 60)}<span className="text-base text-[#4B5563] ml-1">min</span></div>
            <div className="text-xs text-[#4B5563] mt-1">{Math.round((data.voice_credits_used || 0) / 60)} min used</div>
          </div>
          <div className="hs-card p-6">
            <div className="text-xs font-mono uppercase text-[#4B5563] flex items-center gap-1"><Phone className="w-3 h-3" /> phone calling</div>
            <div className="font-display font-extrabold text-3xl mt-2">{data.phone_credits || 0}<span className="text-base text-[#4B5563] ml-1">min</span></div>
            <div className="text-xs text-[#4B5563] mt-1">{data.phone_credits_used || 0} min used · scrapes left: {data.extra_scrapes || 0}</div>
          </div>
        </div>

        {/* Detailed usage breakdown */}
        {usage && (
          <div className="hs-card p-6 space-y-5" data-testid="usage-breakdown">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#0044FF]" />
              <div className="font-display font-bold text-lg">Credit usage this cycle</div>
            </div>

            {/* AI + Voice progress bars */}
            <div className="grid md:grid-cols-2 gap-5">
              <UsageBar
                icon={<Zap className="w-3.5 h-3.5" />}
                label="AI credits (chat)"
                used={usage.ai.used}
                total={usage.ai.total}
                remaining={usage.ai.remaining}
                pct={usage.ai.pct_used}
                color="#0044FF"
              />
              <UsageBar
                icon={<Mic className="w-3.5 h-3.5" />}
                label="Voice credits (mins)"
                used={usage.voice.used}
                total={usage.voice.total}
                remaining={usage.voice.remaining}
                pct={usage.voice.pct_used}
                color="#10B981"
              />
            </div>

            {/* By channel */}
            <div>
              <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Burnt by channel</div>
              <div className="grid sm:grid-cols-3 gap-2">
                {Object.keys(usage.by_channel).length === 0 ? (
                  <div className="text-xs text-[#4B5563] col-span-3">No activity yet this cycle.</div>
                ) : (
                  Object.entries(usage.by_channel).map(([key, v]) => (
                    <div key={key} className="border border-[#E2E8F0] p-3 rounded-sm" data-testid={`usage-channel-${key}`}>
                      <div className="text-[11px] font-mono uppercase text-[#4B5563] flex items-center gap-1.5">
                        {channelIcon(key)} {channelLabel(key)}
                      </div>
                      <div className="font-display font-extrabold text-2xl mt-1">{v.credits}</div>
                      <div className="text-[10px] text-[#4B5563]">credits · {v.events} events</div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* By agent */}
            {usage.by_agent.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Burnt by agent</div>
                <div className="space-y-1.5">
                  {usage.by_agent.map((a) => {
                    const maxCr = usage.by_agent[0]?.credits || 1;
                    const pct = Math.max(2, Math.round((a.credits / maxCr) * 100));
                    return (
                      <div key={a.agent_id || a.name} className="flex items-center gap-3 text-xs" data-testid={`usage-agent-${a.agent_id}`}>
                        <div className="w-40 truncate font-medium">{a.name}</div>
                        <div className="flex-1 bg-[#F3F4F6] h-3 rounded-sm overflow-hidden">
                          <div className="h-3 bg-[#0044FF]" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-20 text-right font-mono text-[11px]">{a.credits} cr</div>
                        <div className="w-16 text-right text-[#4B5563] text-[10px]">{a.events} evt</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Last 14 days */}
            {usage.daily.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase text-[#4B5563] mb-2">Last 14 days</div>
                <div className="flex items-end gap-1 h-24">
                  {(() => {
                    const max = Math.max(...usage.daily.map((d) => d.credits), 1);
                    return usage.daily.map((d) => (
                      <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.credits} credits`}>
                        <div className="w-full bg-[#0044FF]" style={{ height: `${Math.max(4, (d.credits / max) * 80)}px` }} />
                        <div className="text-[9px] font-mono text-[#4B5563]">{d.date.slice(5)}</div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cost & resale pricing */}
        {cost && (
          <div className="hs-card p-6 space-y-5" data-testid="cost-analysis">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#10B981]" />
                <div className="font-display font-bold text-lg">Cost-to-serve &amp; resale pricing</div>
              </div>
              <div className="text-[10px] font-mono text-[#4B5563]">{cost.rate_basis}</div>
            </div>

            {/* Per-channel rate table */}
            <div className="grid md:grid-cols-3 gap-3">
              {/* Web Chat */}
              <div className="border border-[#E2E8F0] p-4 rounded-sm" data-testid="cost-row-web_chat">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-[#4B5563]"><MessageSquare className="w-3 h-3" /> Web Chat</div>
                <div className="font-display font-extrabold text-2xl mt-2">${cost.rates.web_chat.estimated_per_message_usd.toFixed(5)}<span className="text-xs font-normal text-[#4B5563]">/msg</span></div>
                <div className="text-[11px] font-mono text-[#4B5563]">${cost.rates.web_chat.blended_per_1k_tokens.toFixed(5)}/1k tokens</div>
                <div className="text-[10px] text-[#4B5563] mt-1">{cost.rates.web_chat.model} · ~{cost.rates.web_chat.avg_tokens_per_message} tok/msg avg</div>
                <div className="border-t border-[#E2E8F0] mt-3 pt-2">
                  <div className="text-[10px] font-mono uppercase text-[#10B981] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> resale @ {cost.suggested_resale.web_chat.markup_x}x</div>
                  <div className="font-display font-bold text-base">${cost.suggested_resale.web_chat.per_1000_messages_usd.toFixed(2)}<span className="text-xs font-normal text-[#4B5563]">/1000 msgs</span></div>
                </div>
                {cost.actuals.web_chat && (
                  <div className="text-[11px] mt-2 border-t border-[#E2E8F0] pt-2">
                    <div className="font-mono text-[#4B5563]">this cycle</div>
                    <div className="font-bold">${cost.actuals.web_chat.cost_to_serve_usd.toFixed(4)} <span className="font-normal text-[#4B5563]">on {cost.actuals.web_chat.messages} msgs · {cost.actuals.web_chat.tokens} tok</span></div>
                  </div>
                )}
              </div>

              {/* Web Voice */}
              <div className="border border-[#E2E8F0] p-4 rounded-sm" data-testid="cost-row-web_voice">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-[#4B5563]"><Mic className="w-3 h-3" /> Web Voice</div>
                <div className="font-display font-extrabold text-2xl mt-2">${cost.rates.web_voice.per_minute_usd.toFixed(3)}<span className="text-xs font-normal text-[#4B5563]">/min</span></div>
                <div className="text-[11px] font-mono text-[#4B5563]">${cost.rates.web_voice.per_second_usd.toFixed(5)}/sec</div>
                <div className="text-[10px] text-[#4B5563] mt-1">{cost.rates.web_voice.model} · audio-in + audio-out</div>
                <div className="border-t border-[#E2E8F0] mt-3 pt-2">
                  <div className="text-[10px] font-mono uppercase text-[#10B981] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> resale @ {cost.suggested_resale.web_voice.markup_x}x</div>
                  <div className="font-display font-bold text-base">${cost.suggested_resale.web_voice.per_minute_usd.toFixed(2)}<span className="text-xs font-normal text-[#4B5563]">/min</span></div>
                </div>
                {cost.actuals.web_voice && (
                  <div className="text-[11px] mt-2 border-t border-[#E2E8F0] pt-2">
                    <div className="font-mono text-[#4B5563]">this cycle</div>
                    <div className="font-bold">${cost.actuals.web_voice.cost_to_serve_usd.toFixed(4)} <span className="font-normal text-[#4B5563]">on {cost.actuals.web_voice.minutes} min · {cost.actuals.web_voice.sessions} sessions</span></div>
                  </div>
                )}
              </div>

              {/* Phone Calling */}
              <div className="border border-[#E2E8F0] p-4 rounded-sm" data-testid="cost-row-voice_calling">
                <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-[#4B5563]"><Phone className="w-3 h-3" /> Phone Calling</div>
                <div className="font-display font-extrabold text-2xl mt-2">${cost.rates.voice_calling.per_minute_usd.toFixed(3)}<span className="text-xs font-normal text-[#4B5563]">/min</span></div>
                <div className="text-[11px] font-mono text-[#4B5563]">Twilio + VAPI + LLM</div>
                <div className="text-[10px] text-[#4B5563] mt-1 leading-snug">
                  Twilio ${cost.rates.voice_calling.twilio_per_minute_usd}/min · VAPI ${cost.rates.voice_calling.vapi_per_minute_usd}/min · LLM ${cost.rates.voice_calling.llm_per_minute_usd}/min
                </div>
                <div className="border-t border-[#E2E8F0] mt-3 pt-2">
                  <div className="text-[10px] font-mono uppercase text-[#10B981] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> resale @ {cost.suggested_resale.voice_calling.markup_x}x</div>
                  <div className="font-display font-bold text-base">${cost.suggested_resale.voice_calling.per_minute_usd.toFixed(2)}<span className="text-xs font-normal text-[#4B5563]">/min</span></div>
                </div>
                {cost.actuals.voice_calling && (
                  <div className="text-[11px] mt-2 border-t border-[#E2E8F0] pt-2">
                    <div className="font-mono text-[#4B5563]">this cycle (real VAPI cost)</div>
                    <div className="font-bold">${cost.actuals.voice_calling.cost_to_serve_usd.toFixed(4)} <span className="font-normal text-[#4B5563]">on {cost.actuals.voice_calling.minutes} min · {cost.actuals.voice_calling.calls} calls</span></div>
                  </div>
                )}
              </div>
            </div>

            {/* Total */}
            <div className="border-t border-[#E2E8F0] pt-3 flex items-center justify-between">
              <div className="text-xs text-[#4B5563]">Total cost-to-serve this cycle (all channels)</div>
              <div className="font-display font-extrabold text-2xl">${cost.total_cost_to_serve_usd.toFixed(4)}</div>
            </div>
          </div>
        )}

        <div>
          <div className="font-display font-bold text-xl mb-3">Subscription plans</div>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.plans.map((p) => (
              <div key={p.id} className={`hs-card p-6 ${p.popular ? "ring-2 ring-[#0044FF]" : ""}`} data-testid={`plan-card-${p.id}`}>
                {p.popular && <div className="text-xs font-mono text-[#0044FF] mb-2">most popular</div>}
                <div className="font-display font-bold text-2xl">{p.name}</div>
                <div className="font-display font-extrabold text-4xl mt-2">${p.price_usd}<span className="text-base font-mono font-normal text-[#4B5563]">/mo</span></div>
                <ul className="mt-4 text-sm space-y-1.5">
                  {p.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#0044FF] mt-0.5" />{f}</li>)}
                </ul>
                <div className="mt-6 space-y-2">
                  <button data-testid={`subscribe-${p.id}-button`} onClick={() => stripeCheckout("subscription", p.id)} className="btn-primary w-full">{data.package.id === p.id ? "Current (renew via card)" : "Pay with card (Stripe)"}</button>
                  <div data-testid={`paypal-subscription-${p.id}`}>
                    <PayPalPay type="subscription" item_id={p.id} onCaptured={load} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="font-display font-bold text-xl mb-3">Top up credits</div>
          <div className="grid md:grid-cols-3 gap-4">
            {packages.topups.map((t) => (
              <div key={t.id} className="hs-card p-6" data-testid={`topup-card-${t.id}`}>
                <div className="font-display font-bold">{t.name}</div>
                <div className="font-display font-extrabold text-3xl mt-2">${t.price_usd}</div>
                <div className="mt-4 space-y-2">
                  <button data-testid={`topup-${t.id}-button`} onClick={() => stripeCheckout("topup", t.id)} className="btn-outline w-full">Pay with card (Stripe)</button>
                  <div data-testid={`paypal-topup-${t.id}`}>
                    <PayPalPay type="topup" item_id={t.id} onCaptured={load} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hs-card overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E2E8F0] font-display font-bold">Invoices</div>
          {data.invoices.length === 0 ? (
            <div className="p-6 text-sm text-[#4B5563]">No invoices yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
                <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Provider</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Item</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-[#E2E8F0]" data-testid={`invoice-row-${inv.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{inv.created_at?.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-xs">{inv.provider || "stripe"}</td>
                    <td className="px-4 py-3 text-xs">{inv.type}</td>
                    <td className="px-4 py-3 text-xs">{inv.item_id}</td>
                    <td className="px-4 py-3 text-right font-mono">${inv.amount_usd?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-xs">{inv.payment_status || inv.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PayPalScriptProvider>
  );
}

function UsageBar({ icon, label, used, total, remaining, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-mono uppercase text-[#4B5563] flex items-center gap-1.5">{icon} {label}</div>
        <div className="text-[11px] font-mono"><span className="font-bold text-[#030712]">{used}</span> <span className="text-[#4B5563]">/ {total} used</span></div>
      </div>
      <div className="w-full bg-[#F3F4F6] h-3 overflow-hidden rounded-sm">
        <div className="h-3 transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
      <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-[#4B5563]">
        <span>{pct}% spent</span>
        <span>{remaining} remaining</span>
      </div>
    </div>
  );
}

function channelIcon(key) {
  if (key === "web_chat") return <MessageSquare className="w-3 h-3" />;
  if (key === "widget_chat") return <MessageSquare className="w-3 h-3" />;
  if (key === "web_voice") return <Mic className="w-3 h-3" />;
  if (key === "voice_calling") return <Phone className="w-3 h-3" />;
  return <Zap className="w-3 h-3" />;
}

function channelLabel(key) {
  const map = {
    web_chat: "Web Chat (builder)",
    widget_chat: "Web Chat (widget)",
    web_voice: "Web Voice",
    voice_calling: "Phone Calling",
    chat_message: "Chat (legacy)",
  };
  return map[key] || key;
}
